import React, { useEffect, useRef } from 'react';
import { useThemeMode } from '../../theme/ThemeContext';

/**
 * Full-viewport "code rain" canvas — falling code glyphs behind the whole
 * site. Theme-aware, ~20fps, pauses when the tab is hidden and renders
 * nothing at all under prefers-reduced-motion.
 */
const GLYPHS = '{}[]()<>=+-*/;:.&|!#$%_~01λ</>fn=>';

const CodeRain = () => {
  const canvasRef = useRef(null);
  const { theme } = useThemeMode();
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const FONT = 14;
    const GAP = 24;
    let w = 0;
    let h = 0;
    let drops = [];

    const size = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      const cols = Math.ceil(w / GAP);
      drops = Array.from({ length: cols }, () => Math.random() * (-h / FONT));
    };
    size();

    let raf = 0;
    let last = 0;
    let running = true;

    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      if (t - last < 50) return; // ~20fps is plenty for a backdrop
      last = t;

      const dark = themeRef.current === 'dark';
      // Fade previous frame out while keeping the canvas transparent.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
      ctx.font = `${FONT}px Consolas, 'Courier New', monospace`;

      for (let i = 0; i < drops.length; i++) {
        const ch = GLYPHS[(Math.random() * GLYPHS.length) | 0];
        const x = i * GAP;
        const y = drops[i] * FONT;
        ctx.fillStyle =
          Math.random() < 0.05
            ? (dark ? 'rgba(196,181,253,0.85)' : 'rgba(99,102,241,0.5)')
            : (dark ? 'rgba(129,140,248,0.4)' : 'rgba(99,102,241,0.25)');
        ctx.fillText(ch, x, y);
        if (y > h && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 1;
      }
    };
    raf = requestAnimationFrame(draw);

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        running = false;
      } else if (!running) {
        running = true;
        last = 0;
        raf = requestAnimationFrame(draw);
      }
    };

    window.addEventListener('resize', size);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        opacity: 'var(--rain-op, 0.5)',
      }}
    />
  );
};

export default CodeRain;
